import {Component, h, Host, Prop, Event, Element} from '@stencil/core';
import type {EventEmitter} from '@stencil/core';
import { makeLogger } from '../../../logger/logger';
import type {AnalyticsGenericEvent} from '../../../utils/analytics/AnalyticsGenericEvent';

const { err } = makeLogger({
  pathToCurrentFile: 'components/widgets/cta-widget/cta-widget.tsx',
  loggersList: ['err']
});

type ClickType = 'left'|'right'|'keyboard'|'Enter';

export type CtaWidgetClickEvent = {
  category:string
  tabName: string,
  redirectionUrl:string
  clickType:ClickType
};

@Component({
  tag: 'uiaas-search-cta-widget',
  styleUrl: 'cta-widget.scss',
  shadow: true,
})
export class CtaWidget {

  @Event({
    eventName: 'uiaas:widgetLoaded',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    loadEventEmitter:EventEmitter;


  @Event({
    eventName: 'search:ctaWidgetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<CtaWidgetClickEvent>;

  /**
   * Title displayed on top
   */
  @Prop() readonly header: string;

  /**
   * Text displayed in the middle of widget
   */
  @Prop() readonly content: string;

  /**
   * content of bottom link
   */
  @Prop() readonly footer: string;

  /**
   * QDS3 icon name
   */
  @Prop() readonly icon: string;

  /**
   * href attribute of an anchor
   */
  @Prop() readonly url: string;

  /**
   * value used in GA4 events. Should be unique for every type of event
   */
  @Prop() readonly eventCategory: string;

  /**
   * target attribute of an anchor. Allows to open link in new tab
   */
  @Prop() readonly anchorTarget: string = '';

  /**
   * Changes the layout. Sets the title next to icon
   */
  @Prop() readonly isDashboardView: boolean = false;


  /**
   * Changes the layout. Sets the title and the content next to icon
   */
  @Prop() readonly isDashboardMobile: boolean = false;

  /**
   * Value used in GA4 events. By the default component uses parent's tabName. Using this property will override it
   */
  @Prop() readonly tabName: string;

  @Element() el: HTMLUiaasSearchCtaWidgetElement;

  private computedTab: string = '';

  connectedCallback() {
      const closestTabEl = this.el.closest('uiaas-search-tab,dashboard-tab-view-v2');
      this.computedTab = this.tabName || closestTabEl?.['tabName'];
      this.loadEventEmitter.emit();
  }

  private emitSearchStatisticsEvent = (clickType:ClickType, redirectionUrl:string) => {
      this.statisticsEventEmitter.emit({
        category: this.eventCategory,
        tabName: this.computedTab,
        redirectionUrl,
        clickType,
      });
  };

  render() {
      return <a class="link cta-widget-link"
        href={this.url}
        target={this.anchorTarget}
        onMouseDown={e => {this.emitSearchStatisticsEvent(e.button === 0 ? 'left' : 'right', this.url);}}
        onKeyDown={e => {e.key === 'Enter' && this.emitSearchStatisticsEvent(e.key, this.url);}}
      >
        <div class={{
          'cta-widget--mobile ': this.isDashboardMobile,
          'cta-widget--dashboard': this.isDashboardView,
          'cta-widget': true
        }}>
          <div class={{'cta-widget-grid': this.isDashboardMobile || this.isDashboardView}}>
            <div class="icon-large support-icon">
              <slot name="icon">
                <se-icon class="icon-large">{this.icon}</se-icon>
              </slot>
            </div>
            <p class="subtitle1">{this.header}</p>
            <p class="subtitle2">{this.content}</p>
          </div>

          <div class="flex-spacer"></div>
          {this.footer ?
            <div class="cta-widget-footer">
              <qds-standalone-link importance={this.isDashboardMobile ? 'standard' : 'emphasized'}
                class="support-link">{this.footer}</qds-standalone-link>
            </div> : ''
          }
        </div>
      </a>;
  }
}

